import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Subscription, debounceTime, distinctUntilChanged, finalize, forkJoin, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { DisbursementCertificationService } from 'src/app/services/api/disbursement/disbursement.service';
import { OverloadMemoService } from 'src/app/services/api/overload-memo/overload-memo.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import {
  DISBURSEMENT_CALC_DOC_TYPES,
  DISBURSEMENT_DOC_LABELS,
  DISBURSEMENT_SCHEDULE_DOC_TYPES,
  DISBURSEMENT_STATUS_LABELS,
  DisbursementCertificationModel,
  DisbursementDocType,
  DisbursementStatusCode,
} from 'src/app/services/models/disbursement/disbursement.model';
import { OverloadMemoModel } from 'src/app/services/models/overload-memo/overload-memo.model';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import { environment } from 'src/environments/environment';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { SharedModule } from 'src/shared/shared.module';
import {
  buildAcademicTermNameOptions,
  buildAcademicYearOptions,
  defaultAcademicTerm,
  findAcademicTerm,
  toBuddhistYear,
} from 'src/utils/academic-term-options';
import { RoleID } from 'src/utils/enum/role.enum';

// v1.2 §5 (UAM r21 — ข้อมูลเอกสารส่งเบิก) — one row per completed certification
// (scope=completed: pending_bundle/delivered), not per department. Row
// scoping (teacher own / head own dept / others all) is enforced server-side
// by DisbursementCertificationPolicy.scope; this component renders whatever
// the backend returns.
interface RegistryRow {
  id: number;
  teacherName: string;
  departmentName: string;
  academicTermLabel: string;
  pathLabel: string;
  completedAtLabel: string;
  statusCode: DisbursementStatusCode;
  statusLabel: string;
  raw: DisbursementCertificationModel;
}

interface RegistryDataSource {
  data: RegistryRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

// สารบัญ-only block: a department's completed-cert count for the current
// term, downloadable as one ZIP (Page A memo + one merged PDF per cert) via
// the C2 department_zip endpoint.
interface DepartmentZipRow {
  departmentId: number;
  departmentName: string;
  completedCount: number;
}

const STATUS_BADGE_CLASS: Partial<Record<DisbursementStatusCode, string>> = {
  pending_bundle: 'disb-reg-status-pending',
  delivered: 'disb-reg-status-success',
};

@Component({
  selector: 'app-disbursement-registry',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
  ],
  templateUrl: './registry.component.html',
  styleUrl: './registry.component.scss',
})
export class DisbursementRegistryComponent implements OnInit, OnDestroy {
  @ViewChild('statusTemplate', { static: true })
  statusTemplate!: TemplateRef<unknown>;
  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<unknown>;
  @ViewChild('testProvisionDialog')
  testProvisionDialog!: ConfirmDialogComponent;

  // v1.2 decision 6 — dev-only "ทดสอบ: เปิดรอบเบิกจ่าย" trigger, relocated from
  // the deleted settings/disbursement page. FE visibility only; the backend
  // is the real gate (404 outside dev/local, mirrors the email-login bypass).
  readonly enableTestProvisionTrigger = environment.enableDisbursementTestTrigger;

  filterForm = inject(FormBuilder).group({
    academicTermId: [null as number | null],
    academicTerm: [null as string | null],
    academicYear: [null as string | null],
    keywords: [''],
  });

  testProvisionForm = inject(FormBuilder).group({
    termId: [null as number | null],
  });
  testProvisionTermOptions: { id: number; label: string }[] = [];
  testProvisionLoading = signal(false);

  columns: unknown[] = [];
  datasource = signal<RegistryDataSource>(this.createDataSource([]));
  tableLoading = signal(false);
  selectedRow = signal<RegistryRow | null>(null);
  detailOpen = signal(false);
  downloadingDocType = signal<string | null>(null);

  academicTermNameOptions: string[] = [];
  academicYearOptions: string[] = [];

  // สารบัญ extra block (role-gated, same page) — cover-memo card + per-dept
  // ZIP download, covering both the weekly Monday pickup and post-round-4
  // stragglers.
  coverMemo = signal<OverloadMemoModel | null>(null);
  coverMemoLoading = signal(false);
  departmentZipRows = signal<DepartmentZipRow[]>([]);
  departmentZipLoading = signal(false);
  downloadingDepartmentId = signal<number | null>(null);

  private currentPage = 1;
  private currentLimit = 10;
  private currentSortParams: DatatableSortParams = { sort: null, direction: null };
  private academicTerms: AcademicTermYearModel[] = [];
  private readonly subscriptions = new Subscription();
  private readonly sortFieldMap: DatatableSortFieldMap = {
    status: 'status',
    updatedAtLabel: 'updated_at',
  };
  private readonly authService = inject(AuthService);
  private readonly disbursementService = inject(DisbursementCertificationService);
  private readonly overloadMemoService = inject(OverloadMemoService);
  private readonly studyPeriodService = inject(StudyPeriodService);
  private readonly toast = inject(ToastService);

  ngOnInit(): void {
    this.initColumns();
    this.listenFilters();
    this.loadFilterOptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get isDocumentOfficerRole(): boolean {
    return this.authService.getActiveRoleId() === RoleID.DOCUMENT_OFFICER;
  }

  statusBadgeClass(status: DisbursementStatusCode): string {
    return STATUS_BADGE_CLASS[status] ?? 'disb-reg-status-pending';
  }

  onSearch(): void {
    this.loadRegistry(1);
  }

  onView(row: RegistryRow): void {
    this.selectedRow.set(row);
    this.detailOpen.set(true);
  }

  onCloseDetail(): void {
    this.detailOpen.set(false);
    this.selectedRow.set(null);
  }

  // "เอกสารส่งเบิกที่คำนวณแล้ว" shows only the cert's own path's docs
  // (decision 8) — doc_2+doc_5 for a regular cert, doc_3+doc_4 for an
  // international one.
  calcSectionDocTypes(row: RegistryRow | null): DisbursementDocType[] {
    if (row == null) {
      return [];
    }

    return DISBURSEMENT_CALC_DOC_TYPES[row.raw.path];
  }

  // "ตารางเรียนตารางสอน" — doc_7, same for every path.
  scheduleSectionDocTypes(): DisbursementDocType[] {
    return DISBURSEMENT_SCHEDULE_DOC_TYPES;
  }

  docLabel(docType: string): string {
    return DISBURSEMENT_DOC_LABELS[docType] ?? docType;
  }

  isDownloadingDoc(docType: string): boolean {
    return this.downloadingDocType() === docType;
  }

  // Persisted final for a completed cert — always available once a cert
  // reaches pending_bundle/delivered (decision 2's display gate).
  onDownloadDocument(docType: DisbursementDocType): void {
    const row = this.selectedRow();
    if (row == null || this.downloadingDocType() != null) {
      return;
    }

    this.downloadingDocType.set(docType);
    this.disbursementService.downloadDocument(row.id, docType).pipe(
      take(1),
      finalize(() => this.downloadingDocType.set(null)),
    ).subscribe({
      next: (blob) => this.downloadBlob(blob, `${docType}_${row.id}.pdf`),
      error: (err) => {
        console.error(err);
        this.toast.toastError(this.extractErrorMessage(err));
      },
    });
  }

  // The per-cert merged PDF ("เอกสารรวมพร้อมส่งเบิก").
  onDownloadMerged(): void {
    const row = this.selectedRow();
    if (row == null || this.downloadingDocType() != null) {
      return;
    }

    this.downloadingDocType.set('merged');
    this.disbursementService.downloadMergedDocument(row.id).pipe(
      take(1),
      finalize(() => this.downloadingDocType.set(null)),
    ).subscribe({
      next: (blob) => this.downloadBlob(blob, `merged_${row.id}.pdf`),
      error: (err) => {
        console.error(err);
        this.toast.toastError(this.extractErrorMessage(err));
      },
    });
  }

  onDownloadCoverMemo(): void {
    const memo = this.coverMemo();
    if (memo?.status_code !== 'certified') {
      return;
    }

    this.overloadMemoService.downloadOverloadMemo(memo.id).pipe(take(1)).subscribe((blob) => {
      this.downloadBlob(blob, `overload_memo_${memo.id}.pdf`);
    });
  }

  coverMemoStatusLabel(): string {
    switch (this.coverMemo()?.status_code) {
      case 'certified':
        return 'รับรองแล้ว';
      case 'revision_requested':
        return 'ตีกลับแก้ไข';
      case 'pending_vice_dean':
      case 'pending_education_head':
        return 'รอรับรอง';
      case 'draft':
      case null:
      case undefined:
      default:
        return 'ยังไม่ได้จัดทำ';
    }
  }

  coverMemoStatusClass(): string {
    switch (this.coverMemo()?.status_code) {
      case 'certified':
        return 'disb-reg-cover-memo-status-certified';
      case 'revision_requested':
        return 'disb-reg-cover-memo-status-revision';
      case 'pending_vice_dean':
      case 'pending_education_head':
      case 'draft':
      case null:
      case undefined:
      default:
        return 'disb-reg-cover-memo-status-pending';
    }
  }

  onDownloadDepartmentZip(row: DepartmentZipRow): void {
    const academicTermId = this.filterForm.getRawValue().academicTermId;
    if (academicTermId == null || this.downloadingDepartmentId() != null) {
      return;
    }

    this.downloadingDepartmentId.set(row.departmentId);
    this.disbursementService.downloadDepartmentZip(academicTermId, row.departmentId).pipe(
      take(1),
      finalize(() => this.downloadingDepartmentId.set(null)),
    ).subscribe({
      next: (blob) => this.downloadBlob(blob, `department_zip_${academicTermId}_${row.departmentId}.zip`),
      error: (err) => {
        console.error(err);
        this.toast.toastError(this.extractErrorMessage(err));
      },
    });
  }

  // v1.2 decision 6 — dev-only re-provision trigger. FE only renders this
  // when environment.enableDisbursementTestTrigger is on; the backend
  // 404s the request itself when it isn't (see disbursement.service.ts).
  onOpenTestProvisionDialog(): void {
    if (this.testProvisionForm.getRawValue().termId == null) {
      return;
    }

    this.testProvisionDialog.open();
  }

  onConfirmTestProvision(): void {
    const termId = this.testProvisionForm.getRawValue().termId;
    if (termId == null) {
      return;
    }

    this.testProvisionLoading.set(true);
    this.disbursementService.testProvision(termId).pipe(
      finalize(() => this.testProvisionLoading.set(false)),
    ).subscribe({
      next: (result) => {
        this.testProvisionDialog.close();
        this.toast.toastSuccess(`เปิดรอบเบิกจ่ายแล้ว (${result.certifications_count} ใบรับรอง)`);
        this.loadRegistry(this.currentPage);
        this.loadDepartmentZipBlock();
      },
      error: (err) => {
        console.error(err);
        this.toast.toastError(this.extractErrorMessage(err));
      },
    });
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.datasource().total_page || page === this.currentPage) {
      return;
    }

    this.loadRegistry(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadRegistry(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadRegistry(1);
  }

  formatDate(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return [
      String(date.getDate()).padStart(2, '0'),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(toBuddhistYear(date.getFullYear())),
    ].join('/');
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'teacherName',
        name: 'อาจารย์',
        sortable: false,
        width: 190,
        minWidth: 170,
      },
      {
        prop: 'pathLabel',
        name: 'ภาค',
        sortable: false,
        width: 110,
        minWidth: 100,
      },
      {
        prop: 'departmentName',
        name: 'สาขาวิชา',
        sortable: false,
        width: 170,
        minWidth: 150,
      },
      {
        prop: 'academicTermLabel',
        name: 'ภาคการศึกษา',
        sortable: false,
        width: 120,
        minWidth: 110,
      },
      {
        prop: 'completedAtLabel',
        name: 'วันที่เสร็จสมบูรณ์',
        sortable: false,
        width: 140,
        minWidth: 130,
      },
      {
        prop: 'status',
        name: 'สถานะ',
        sortable: true,
        cellTemplate: this.statusTemplate,
        width: 180,
        minWidth: 170,
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.actionTemplate,
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        cellClass: 'text-center disb-reg-action-cell',
        headerClass: 'text-center disb-reg-action-header',
      },
    ];
  }

  private listenFilters(): void {
    this.subscriptions.add(
      this.filterForm.valueChanges
        .pipe(debounceTime(250), distinctUntilChanged())
        .subscribe(() => this.loadRegistry(1)),
    );
    this.subscriptions.add(
      this.filterForm.controls.academicYear.valueChanges.subscribe(() => this.onAcademicYearChanged()),
    );
    this.subscriptions.add(
      this.filterForm.controls.academicTerm.valueChanges.subscribe(() => this.syncAcademicTermId()),
    );
  }

  private onAcademicYearChanged(): void {
    const { academicYear, academicTerm } = this.filterForm.getRawValue();
    this.academicTermNameOptions = buildAcademicTermNameOptions(this.academicTerms, academicYear);
    const nextTerm = academicTerm != null && this.academicTermNameOptions.includes(academicTerm)
      ? academicTerm
      : this.academicTermNameOptions[0] ?? null;
    this.filterForm.patchValue({ academicTerm: nextTerm }, { emitEvent: false });
    this.syncAcademicTermId();
  }

  private syncAcademicTermId(): void {
    const { academicTerm, academicYear } = this.filterForm.getRawValue();
    const resolved = findAcademicTerm(this.academicTerms, academicTerm, academicYear);
    this.filterForm.patchValue({ academicTermId: resolved?.id ?? null }, { emitEvent: false });
  }

  private loadFilterOptions(): void {
    this.studyPeriodService.getAcademicTermYears().pipe(take(1)).subscribe({
      next: (terms) => {
        this.academicTerms = terms;
        this.academicYearOptions = buildAcademicYearOptions(terms);
        this.testProvisionTermOptions = terms.map((term) => ({
          id: term.id,
          label: `${term.name}/${toBuddhistYear(term.year)}`,
        }));
        const defaultTerm = defaultAcademicTerm(terms);
        const defaultYear = defaultTerm != null ? String(toBuddhistYear(defaultTerm.year)) : null;
        this.academicTermNameOptions = buildAcademicTermNameOptions(terms, defaultYear);
        this.filterForm.patchValue(
          {
            academicYear: defaultYear,
            academicTerm: defaultTerm != null ? String(defaultTerm.name).trim() : null,
            academicTermId: defaultTerm?.id ?? null,
          },
          { emitEvent: false },
        );
        this.testProvisionForm.patchValue({ termId: defaultTerm?.id ?? null }, { emitEvent: false });
        this.loadRegistry(1);
      },
      error: (err) => {
        console.error(err);
        this.loadRegistry(1);
      },
    });
  }

  private loadRegistry(page: number): void {
    this.currentPage = page;
    this.tableLoading.set(true);
    this.disbursementService.getCertifications({
      ...this.buildFilterParams(),
      scope: 'completed',
      page,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort as 'status' | 'updated_at' | null,
      direction: this.currentSortParams.direction,
    }).pipe(
      finalize(() => this.tableLoading.set(false)),
    ).subscribe({
      next: (response) => {
        this.datasource.set(this.createDataSource(
          response.data.map((cert) => this.mapCertificationToRow(cert)),
          response.page,
          response.limit,
          response.total,
          response.total_pages,
        ));
      },
      error: (err) => {
        console.error(err);
        this.datasource.set(this.createDataSource([], page, this.currentLimit));
      },
    });

    this.loadDepartmentZipBlock();
  }

  // สารบัญ-only block. Only fetched for that role — the per-department ZIP
  // download and cover memo are irrelevant to the other five VE roles.
  private loadDepartmentZipBlock(): void {
    if (!this.isDocumentOfficerRole) {
      return;
    }

    const academicTermId = this.filterForm.getRawValue().academicTermId;
    if (academicTermId == null) {
      this.departmentZipRows.set([]);
      this.coverMemo.set(null);
      return;
    }

    this.departmentZipLoading.set(true);
    this.coverMemoLoading.set(true);

    forkJoin({
      certifications: this.disbursementService.getAllCertifications({ academic_term_id: academicTermId, scope: 'completed' }),
      coverMemos: this.overloadMemoService.getOverloadMemos({ academic_term_id: academicTermId, page: 1, limit: 1 }),
    }).pipe(
      finalize(() => {
        this.departmentZipLoading.set(false);
        this.coverMemoLoading.set(false);
      }),
    ).subscribe({
      next: ({ certifications, coverMemos }) => {
        this.departmentZipRows.set(this.groupByDepartment(certifications));
        this.coverMemo.set(coverMemos.data[0] ?? null);
      },
      error: (err) => {
        console.error(err);
        this.departmentZipRows.set([]);
        this.coverMemo.set(null);
      },
    });
  }

  private groupByDepartment(certifications: DisbursementCertificationModel[]): DepartmentZipRow[] {
    const byDepartment = new Map<number, DisbursementCertificationModel[]>();

    certifications.forEach((cert) => {
      const departmentId = cert.department.id;
      const bucket = byDepartment.get(departmentId) ?? [];
      bucket.push(cert);
      byDepartment.set(departmentId, bucket);
    });

    return Array.from(byDepartment.entries())
      .map(([departmentId, certs]) => ({
        departmentId,
        departmentName: certs[0]?.department.name_th ?? certs[0]?.department.name ?? certs[0]?.department.name_en ?? '-',
        completedCount: certs.length,
      }))
      .sort((a, b) => a.departmentName.localeCompare(b.departmentName, 'th'));
  }

  private buildFilterParams(): { academic_term_id?: number; keywords?: string } {
    const { academicTermId, keywords } = this.filterForm.getRawValue();
    const params: { academic_term_id?: number; keywords?: string } = {};

    if (academicTermId != null) {
      params.academic_term_id = academicTermId;
    }

    const normalizedKeywords = String(keywords ?? '').trim();
    if (normalizedKeywords !== '') {
      params.keywords = normalizedKeywords;
    }

    return params;
  }

  // The completion date shown in the table is when the cert crossed into
  // pending_bundle/delivered — read straight off approval_track's
  // vice_dean_administration station rather than re-deriving it client-side
  // (that station is "done" for every cert this scope can ever return).
  private completedAtLabel(cert: DisbursementCertificationModel): string {
    const station = cert.approval_track.find((entry) => entry.station === 'vice_dean_administration');
    return this.formatDate(station?.acted_at);
  }

  private mapCertificationToRow(cert: DisbursementCertificationModel): RegistryRow {
    return {
      id: cert.id,
      teacherName: cert.user.full_name,
      departmentName: cert.department.name_th ?? cert.department.name ?? cert.department.name_en ?? '-',
      academicTermLabel: `${cert.academic_term.name}/${toBuddhistYear(cert.academic_term.year)}`,
      pathLabel: cert.path_label,
      completedAtLabel: this.completedAtLabel(cert),
      statusCode: cert.status_code,
      statusLabel: DISBURSEMENT_STATUS_LABELS[cert.status_code],
      raw: cert,
    };
  }

  private createDataSource(
    rows: RegistryRow[],
    page = 1,
    limit = 10,
    total = rows.length,
    totalPage = Math.max(Math.ceil(total / limit), 1),
  ): RegistryDataSource {
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);

    return {
      data: rows,
      limit,
      offset: (normalizedPage - 1) * limit,
      page: normalizedPage,
      total,
      total_page: totalPage,
    };
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private extractErrorMessage(err: unknown): string {
    if (err !== null && typeof err === 'object' && 'error' in err) {
      const error = (err as { error?: unknown }).error;
      if (error !== null && typeof error === 'object' && 'errors' in error) {
        const errors = (error as { errors?: unknown }).errors;
        if (Array.isArray(errors) && errors.length > 0) {
          return String(errors[0]);
        }
        if (typeof errors === 'string') {
          return errors;
        }
      }
    }

    return 'ไม่สามารถดำเนินการได้';
  }
}
